/**
 * Pure rules for the public Divisions tab — one list that is the entry list
 * before the event and the draw list once draws are released (the app's 7a
 * divisions screen and its Draw door, folded into one web surface).
 *
 * Every organiser gate is decided here so the component only renders:
 * - hidden registrants → the caller passes `entryCount: undefined`; only a
 *   server-flagged `full` survives (a spots-left figure is one subtraction
 *   from the hidden count, so it never shows either);
 * - hidden competition → `competitionHidden` makes any draw invisible;
 * - only a publicly visible draw (`isDrawPubliclyVisible`) is a draw;
 * - spots-left / Full are registration-window signals, shown only while
 *   registration is open and no draw has taken the row over.
 */
import {
  drawFormatKey,
  sortPublicDraws,
  type DrawFormatKey,
  type DrawPlan,
} from './drawListPresentation';
import { isDrawPubliclyVisible } from '@/lib/appDrawProjection';

export type DivisionPill = 'spotsLeft' | 'full' | 'closed' | 'inProgress' | 'finished';

export interface DivisionRowInput {
  /** Q37: the organiser set the division to anything but `active` (`isDivisionClosed`). */
  closed?: boolean;
  group: { id: string; max_participants: number | null };
  /** The group's public draw, if any (see `publicDrawByGroup`). */
  draw: (DrawPlan & { id: string; status: string; format_type: string | null }) | null;
  /**
   * Every registration row (the capacity rule registration enforces — a
   * withdrawn entry still holds its spot); `undefined` while loading or
   * when registrants are hidden.
   */
  entryCount: number | undefined;
  /** Active rows only: what a released draw actually contains. Falls back to `entryCount`. */
  activeCount?: number | undefined;
  /** Server verdict, valid even when the count is hidden. */
  full: boolean;
  registrationOpen: boolean;
  competitionHidden: boolean;
}

export interface DivisionRow {
  destination: 'draw' | 'division';
  drawId: string | null;
  pill: DivisionPill | null;
  spotsLeft: number | null;
  format: DrawFormatKey | null;
  entries: number | null;
}

function drawPill(status: string): DivisionPill | null {
  switch (status) {
    case 'active':
      return 'inProgress';
    case 'completed':
      return 'finished';
    // A released draw is the normal case; the row's format and link already say it.
    default:
      return null;
  }
}

export function divisionRow(input: DivisionRowInput): DivisionRow {
  const { group, entryCount, registrationOpen, competitionHidden } = input;
  const draw =
    input.draw && !competitionHidden && isDrawPubliclyVisible(input.draw) ? input.draw : null;
  const entries = entryCount ?? null;
  // A falsy cap is "uncapped", the rule registration enforces (eligibility.ts).
  const cap = group.max_participants || null;

  if (draw) {
    return {
      destination: 'draw',
      drawId: draw.id,
      pill: drawPill(draw.status),
      spotsLeft: null,
      format: drawFormatKey(draw.format_type, draw),
      entries: entryCount === undefined ? null : (input.activeCount ?? entries),
    };
  }

  const full = input.full || (entries !== null && cap !== null && entries >= cap);
  let pill: DivisionPill | null = null;
  let spotsLeft: number | null = null;
  if (registrationOpen) {
    if (input.closed) {
      pill = 'closed';
    } else if (full) {
      pill = 'full';
    } else if (entries !== null && cap !== null) {
      pill = 'spotsLeft';
      spotsLeft = cap - entries;
    }
  }

  return { destination: 'division', drawId: null, pill, spotsLeft, format: null, entries };
}

/**
 * One public draw per group (`isDrawPubliclyVisible`), the draw in play
 * preferred over a merely published one, then the public list order.
 */
export function publicDrawByGroup<
  T extends {
    status: string;
    name: string;
    event_group_id: string;
    event_group: { sort_order: number | null };
  },
>(draws: T[]): Record<string, T> {
  const byGroup: Record<string, T> = {};
  for (const draw of sortPublicDraws(draws)) {
    if (!isDrawPubliclyVisible(draw)) continue;
    const current = byGroup[draw.event_group_id];
    if (!current || (draw.status === 'active' && current.status !== 'active')) {
      byGroup[draw.event_group_id] = draw;
    }
  }
  return byGroup;
}

/** Capped groups whose entry count reached the cap — computed server-side so it survives hidden counts. */
export function fullGroupIds(
  counts: Record<string, number>,
  groups: Array<{ id: string; max_participants: number | null }>,
): string[] {
  return groups
    .filter((g) => g.max_participants && (counts[g.id] ?? 0) >= g.max_participants)
    .map((g) => g.id);
}

/** Organiser order (`sort_order`), then name. */
export function sortGroups<T extends { name: string; sort_order: number | null }>(
  groups: T[],
): T[] {
  return [...groups].sort((a, b) => {
    const orderA = a.sort_order ?? 999;
    const orderB = b.sort_order ?? 999;
    if (orderA !== orderB) return orderA - orderB;
    return a.name.localeCompare(b.name);
  });
}
